import React, { useEffect, useState } from 'react'
import { StyleSheet, ViewProps } from 'react-native'
import { Calendar, LocaleConfig, DateData } from 'react-native-calendars'
import {
  format,
  setMonth,
  setDay,
  isSameDay,
  add,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  sub,
  getDay,
  endOfMonth,
  addDays,
  subDays,
} from 'date-fns'

import { dateFormatter, Weekday, AllWeekdays } from '../../helper'
import { Color, Font } from '../../style'
import { Icon } from '../icon'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  today: {
    fontFamily: Font.medium,
    color: Color.greyBlue,
  },
  todayDisabled: {
    color: Color.greyBlue,
  },
})

const theme = {
  textMonthFontFamily: Font.medium,
  monthTextColor: Color.premiumBlack,
  textDayHeaderFontFamily: Font.light,
  textDayHeaderFontSize: 12,
  textSectionTitleColor: Color.metalGrey,
  textDayFontFamily: Font.light,
  textDayFontSize: 12,
  dayTextColor: Color.premiumBlack,
  textDisabledColor: Color.calmGrey,
  'stylesheet.day.period': {
    wrapper: {
      alignItems: 'center',
      alignSelf: 'stretch',
      marginLeft: 0,
    },
    base: {
      width: 34,
      height: 34,
      alignItems: 'center',
      justifyContent: 'center',
    },
    today: {
      cursor: 'pointer',
    },
  },
  weekVerticalMargin: 1,
}

const locale = (language: string) => {
  return {
    monthNames: Array(12)
      .fill('')
      .map((_, index) => dateFormatter(setMonth(Date.now(), index), 'MMMM', language)),
    monthNamesShort: Array(12)
      .fill('')
      .map((_, index) => dateFormatter(setMonth(Date.now(), index), 'MMM', language)),
    dayNames: Array(7)
      .fill('')
      .map((_, index) => dateFormatter(setDay(Date.now(), index, { weekStartsOn: 1 }), 'EEEE', language)),
    dayNamesShort: Array(7)
      .fill('')
      .map((_, index) => dateFormatter(setDay(Date.now(), index, { weekStartsOn: 1 }), 'EEEEE', language)),
    formatAccessibilityLabel: 'i',
  }
}

export type Value = [Date] | [Date, Date]

export interface CalendarDisabled {
  before?: Date
  after?: Date
  weekdays?: Weekday[]
}

export interface Props extends ViewProps {
  language?: string
  value?: Value
  onValueChange?: (value: Value) => void
  date?: Date
  onDateChange?: (date: Date) => void
  disabled?: CalendarDisabled
}
const CalendarView = (props: Props) => {
  const { language = 'en', onValueChange, date, onDateChange, disabled, style, ...otherProps } = props
  const [value, setValue] = useState<Date[]>(props.value || [])
  const [currentMonth, setCurrentMonth] = useState(date)
  const [startAt, endAt] = value
  useEffect(() => {
    setValue(props.value || [])
  }, [props.value])

  const markedDates = () => {
    const todayDisabled =
      (disabled?.after && isAfter(new Date(), addDays(disabled?.after, 1))) ||
      (disabled?.before && isBefore(new Date(), subDays(disabled?.before, 1)))

    const markedDates: Record<string, any> = {
      [format(new Date(), 'yyyy-MM-dd')]: {
        today: true,
        customTextStyle: [styles.today, todayDisabled && styles.todayDisabled],
        disableTouchEvent: todayDisabled,
      },
    }

    if (disabled?.weekdays) {
      const monthStart = startOfMonth(currentMonth || new Date())
      const calendarStart = sub(monthStart, { days: (7 + getDay(monthStart) - 1) % 7 })
      const monthEnd = endOfMonth(currentMonth || new Date())
      const calendarEnd = add(monthEnd, { days: 6 - ((7 + getDay(monthEnd) - 1) % 7) })
      let current = calendarStart
      while (!isAfter(current, calendarEnd)) {
        if (disabled?.weekdays.indexOf(AllWeekdays[getDay(current)]) >= 0) {
          markedDates[format(current, 'yyyy-MM-dd')] = {
            textColor: Color.calmGrey,
            disableTouchEvent: todayDisabled,
          }
        }
        current = add(current, { days: 1 })
      }
    }

    if (startAt) {
      let date = startAt
      do {
        const dayDisabled = disabled?.weekdays && disabled?.weekdays.indexOf(AllWeekdays[getDay(date)]) >= 0
        markedDates[format(date, 'yyyy-MM-dd')] = {
          startingDay: date === startAt,
          endingDay: !endAt || isSameDay(date, endAt),
          color: dayDisabled ? Color.calmGrey : Color.lightGreyBlue,
          textColor: isSameDay(date, new Date())
            ? Color.premiumBlack
            : dayDisabled
              ? Color.metalGrey
              : Color.simplyWhite,
          disableTouchEvent: dayDisabled,
        }
        date = add(date, { days: 1 })
      } while (endAt && !isAfter(startOfDay(date), startOfDay(endAt)))
    }
    return markedDates
  }

  const updateValue = (day: DateData) => {
    const { timestamp } = day
    const date = new Date(timestamp)

    let newValue: Value
    if (value.length === 1) newValue = [value[0], date].sort((a, b) => a.getTime() - b.getTime()) as Value
    else newValue = [date]
    setValue(newValue)
    onValueChange?.(newValue)
  }

  const updateDate = (day: DateData) => {
    const current = startOfMonth(new Date(day.timestamp))
    if (!currentMonth || !isSameDay(current, currentMonth)) {
      onDateChange?.(current)
      setCurrentMonth(current)
    }
  }

  LocaleConfig.locales[language] = locale(language)
  LocaleConfig.defaultLocale = language

  return (
    <Calendar
      style={[styles.container, style]}
      theme={theme}
      initialDate={format(date ? date : new Date(), 'yyyy-MM-dd')}
      firstDay={1}
      renderArrow={(direction) => <Icon name={direction} size="XS" testID={`Calendar.Arrow-${direction}`} />}
      enableSwipeMonths
      markingType="period"
      markedDates={markedDates()}
      onDayPress={updateValue}
      onMonthChange={updateDate}
      minDate={disabled?.before ? format(disabled?.before, 'yyyy-MM-dd') : undefined}
      maxDate={disabled?.after ? format(disabled?.after, 'yyyy-MM-dd') : undefined}
      allowSelectionOutOfRange={false}
      testID="CalendarView.Calendar"
      {...otherProps}
    />
  )
}

export default CalendarView
